import { fromPairs, pipe, map, path, pathEq, mapObjIndexed, curry, pickBy } from 'ramda'
import { useSelector } from 'react-redux'
import { selectors as configSelectors } from 'mage-config'
import useCMSContext from './useCMSContext'
import useCMSRequest from './useCMSRequest'
import useContentRank from './useContentRank'

export default function useCMSEntries({
  contentType,
  sortField,
  filters,
  contentRankLimit,
  include = 1,
  skip = false
}) {
  const region = useSelector(configSelectors.region).toUpperCase()

  const { contentTypesResponse } = useCMSContext()
  const response = useCMSEntriesRequest(contentType, sortField, filters, include, skip)

  const transformedEntries = transform(region, contentTypesResponse, response, include)

  return useContentRank(transformedEntries, contentRankLimit)
}

const getContentTypeId = path(['sys', 'contentType', 'sys', 'id'])

function itemData(region, contentTypes, response, remainingDepth, item) {
  const data = {
    id: item.sys.id,
    contentType: getContentTypeId(item),
    ...item.fields
  }

  return remainingDepth === 0
    ? data
    : {
        ...data,
        ...linkedData(region, contentTypes, response, remainingDepth - 1, item.fields)
      }
}

const isAvailableInRegion = curry((region, { regions }) => !regions || regions.includes(region))

const isTranslated = curry((contentTypes, entry) => {
  const requiredFields = contentTypes
    .find(contentType => contentType.sys.id === entry.contentType)
    .fields.filter(field => field.required)
    .map(field => field.id)

  return requiredFields.every(field => entry[field])
})

function transform(region, contentTypesResponse, response, include) {
  if (!contentTypesResponse || !response) {
    return null
  }
  const contentTypes = contentTypesResponse.items

  return response.items
    .map(item => ({
      ...itemData(region, contentTypes, response, include, item),
      ...documentFields(region, contentTypes, response, item.fields)
    }))
    .filter(isAvailableInRegion(region))
    .filter(isTranslated(contentTypes))
}

function linkedData(region, contentTypes, response, remainingDepth, fields) {
  return mapObjIndexed(
    value =>
      Array.isArray(value)
        ? value
            .map(item =>
              linkedItemIfReference(
                region,
                contentTypes,
                response,
                remainingDepth,
                true,
                true,
                item
              )
            )
            .filter(Boolean)
        : linkedItemIfReference(
            region,
            contentTypes,
            response,
            remainingDepth,
            false,
            false,
            value
          ),
    fields
  )
}

function linkedItemIfReference(
  region,
  contentTypes,
  response,
  remainingDepth,
  checkRegion,
  checkTranslation,
  value
) {
  const isLink = pathEq(['sys', 'type'], 'Link', value)

  if (isLink) {
    const linkedItem = getLinkedItem(
      region,
      contentTypes,
      response,
      remainingDepth,
      value.sys.linkType,
      value.sys.id
    )

    if (!linkedItem) {
      // Entry is unpublished
      return null
    }

    if (checkRegion && !isAvailableInRegion(region, linkedItem)) {
      // Entry is not available in region
      return null
    }

    if (checkTranslation && !isTranslated(contentTypes, linkedItem)) {
      // Entry is missing translation
      return null
    }

    return linkedItem
  } else {
    return value
  }
}

function getLinkedItem(region, contentTypes, response, remainingDepth, linkType, id) {
  let entries = []
  if (response.items && response.includes && response.includes[linkType]) {
    entries = response.items.concat(response.includes[linkType])
  } else if (!response.items && response.includes && response.includes[linkType]) {
    entries = response.includes[linkType]
  } else if (response.items) {
    entries = response.items
  }
  const entry = entries.find(entry => entry.sys.id === id)
  return entry ? itemData(region, contentTypes, response, remainingDepth, entry) : null
}

function documentFields(region, contentTypes, response, fields) {
  return pipe(
    pickBy(value => value.nodeType === 'document'),
    mapValues(value => linkContentItem(region, contentTypes, response, value))
  )(fields)
}

function linkContent(region, contentTypes, response, content) {
  return content.map(contentItem => linkContentItem(region, contentTypes, response, contentItem))
}

function linkContentItem(region, contentTypes, response, contentItem) {
  return {
    ...contentItem,
    ...(contentItem.content
      ? { content: linkContent(region, contentTypes, response, contentItem.content) }
      : {}),
    data: {
      ...contentItem.data,
      ...(['entry-hyperlink', 'asset-hyperlink', 'embedded-asset-block'].includes(
        contentItem.nodeType
      )
        ? {
            target: getLinkedItem(
              region,
              contentTypes,
              response,
              0,
              contentItem.data.target.sys.linkType,
              contentItem.data.target.sys.id
            )
          }
        : {})
    }
  }
}

function useCMSEntriesRequest(contentType, sortField, filters, include, skip) {
  const reservedFields = ['links_to_entry', 'query']

  const filterFields = pipe(
    mapKeys(key => (reservedFields.includes(key) ? key : `fields.${key}`)),
    mapValues(value => (Array.isArray(value) ? value.join(',') : value))
  )(filters || {})

  return useCMSRequest(
    `/entries`,
    {
      select: 'fields,sys.id,sys.contentType',
      content_type: contentType,
      include,
      ...filterFields,
      ...(sortField && { order: `fields.${sortField}` })
    },
    skip
  )
}

const mapKeys = iterator =>
  pipe(
    Object.entries,
    map(([key, value]) => [iterator(key), value]),
    fromPairs
  )

const mapValues = iterator =>
  pipe(
    Object.entries,
    map(([key, value]) => [key, iterator(value)]),
    fromPairs
  )
